import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import CTASection from "../components/CTASection";
import { POSTS, formatDate } from "../data/blog";

function Block({ block }) {
  if (block.type === "h2") {
    return <h2 className="mt-12 font-display text-2xl font-medium tracking-tight text-[#07111F] sm:text-3xl">{block.text}</h2>;
  }
  if (block.type === "quote") {
    return (
      <blockquote className="my-10 border-l-2 border-blue-600 pl-6">
        <p className="font-display text-xl font-light leading-relaxed tracking-tight text-[#07111F] sm:text-2xl">{block.text}</p>
      </blockquote>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="mt-6 space-y-3">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-3 text-base leading-relaxed text-slate-600">
            <span className="mt-[0.6rem] h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">{block.text}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  useSEO({
    title: post ? `${post.title} | SefKam Insights` : "SefKam Insights",
    description: post ? post.excerpt : "",
    path: post ? `/blog/${post.slug}` : "/blog",
  });
  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 2);
  const fill = POSTS.filter((p) => p.slug !== post.slug && !related.includes(p)).slice(0, 2 - related.length);
  const more = [...related, ...fill];

  return (
    <>
      <section className="relative overflow-hidden bg-[#07111F] pt-36 pb-16 md:pt-44 md:pb-20 grain" data-testid="blogpost-hero">
        <div className="pointer-events-none absolute -top-32 right-1/4 h-[30rem] w-[30rem] rounded-full opacity-20 blur-3xl accent-bg" />
        <div className="container-x relative max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white" data-testid="blogpost-back">
              <ArrowLeft size={15} /> All insights
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08 }}
            className="mt-8 flex flex-wrap items-center gap-3 text-xs text-slate-400"
          >
            <span className="rounded-full border border-white/15 px-3 py-1 font-medium text-cyan-300">{post.category}</span>
            <span>{formatDate(post.date)}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.14, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 font-display text-3xl font-medium leading-[1.1] tracking-tighter text-white sm:text-4xl lg:text-5xl"
            data-testid="blogpost-title"
          >
            {post.title}
          </motion.h1>
        </div>
      </section>

      <article className="bg-white py-16 md:py-24" data-testid="blogpost-content">
        <div className="container-x max-w-3xl">
          <p className="font-display text-xl font-light leading-relaxed tracking-tight text-[#07111F] sm:text-2xl">{post.excerpt}</p>
          <div className="mt-4">
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
          <div className="mt-14 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 text-sm leading-relaxed text-slate-500">
            Written by the <span className="font-medium text-[#07111F]">SefKam engineering team</span>. We build AI, automation and software systems for businesses — and we write about what we learn along the way.
          </div>
        </div>
      </article>

      <section className="bg-[#F8FAFC] py-16 md:py-20" data-testid="blogpost-related">
        <div className="container-x">
          <span className="overline text-slate-400">Keep reading</span>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {more.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(7,17,31,0.15)]"
                data-testid={`related-${p.slug}`}
              >
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="rounded-full border border-slate-200 bg-[#F8FAFC] px-3 py-1 font-medium text-[#07111F]">{p.category}</span>
                  <span>{p.readTime}</span>
                </div>
                <h3 className="mt-4 font-display text-xl font-medium tracking-tight text-[#07111F] transition-colors group-hover:text-blue-700">{p.title}</h3>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#07111F]">
                  Read article <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
